/**
 * WorkspaceChart — renders chart content blocks in metadata-driven workspaces.
 *
 * A chart is defined in workspace JSON:
 * {
 *   "type": "chart",
 *   "chartType": "LINE",         // LINE, BAR, GAUGE, NUMBER, PIE, AREA, STACKED_BAR
 *   "title": "Picks Per Hour",
 *   "dataScript": "kpi_picks_per_hour",
 *   "xAxis": { "field": "date", "label": "Date" },
 *   "yAxis": { "field": "value", "label": "Picks/Hr" },
 *   "thresholds": { "target": 50, "warning": 35, "critical": 20 },
 *   "height": 250,
 *   "refreshInterval": 300
 * }
 *
 * Data is loaded by executing the FLEX script specified in dataScript.
 * The script returns the data shape expected by the chart type.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Box, Typography, CircularProgress } from '@mui/material';
import {
  LineChart, Line, BarChart, Bar, PieChart, Pie, Cell,
  AreaChart, Area,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  ReferenceLine,
} from 'recharts';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import TrendingFlatIcon from '@mui/icons-material/TrendingFlat';
import FlexService from '../../services/FlexService';

const CHART_COLORS = ['#22c55e', '#3b82f6', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4', '#ec4899', '#f97316'];

const getThresholdColor = (value, thresholds) => {
  if (!thresholds) return '#22c55e';
  // For metrics where higher is better (picks/hr, SLA%)
  if (thresholds.target >= (thresholds.critical || 0)) {
    if (value >= thresholds.target) return '#22c55e';
    if (value >= (thresholds.warning || thresholds.target * 0.8)) return '#f59e0b';
    return '#ef4444';
  }
  // For metrics where lower is better (risk score, queue depth)
  if (value <= thresholds.target) return '#22c55e';
  if (value <= (thresholds.warning || thresholds.target * 1.5)) return '#f59e0b';
  return '#ef4444';
};

// ========== Chart Renderers ==========

const LineChartRenderer = ({ config, data, colors }) => {
  const series = config.seriesFields || [config.yAxis?.field || 'value'];
  const labels = config.seriesLabels || {};
  const height = config.height || 250;

  return (
    <ResponsiveContainer width="100%" height={height}>
      <LineChart data={data} margin={{ top: 5, right: 20, bottom: 5, left: 0 }}>
        <CartesianGrid strokeDasharray="3 3" stroke={colors.border} />
        <XAxis dataKey={config.xAxis?.field || 'date'} tick={{ fill: colors.textMuted, fontSize: 11 }} stroke={colors.border} />
        <YAxis tick={{ fill: colors.textMuted, fontSize: 11 }} stroke={colors.border} />
        <Tooltip contentStyle={{ background: colors.surface, border: `1px solid ${colors.border}`, borderRadius: 4, color: colors.text }} />
        {series.length > 1 && <Legend wrapperStyle={{ color: colors.textSecondary, fontSize: 11 }} />}
        {config.thresholds?.target && (
          <ReferenceLine y={config.thresholds.target} stroke="#22c55e" strokeDasharray="5 5" label={{ value: 'Target', fill: '#22c55e', fontSize: 10 }} />
        )}
        {config.thresholds?.warning && (
          <ReferenceLine y={config.thresholds.warning} stroke="#f59e0b" strokeDasharray="3 3" />
        )}
        {series.map((field, i) => (
          <Line key={field} type="monotone" dataKey={field} name={labels[field] || field}
            stroke={CHART_COLORS[i % CHART_COLORS.length]} strokeWidth={2} dot={{ r: 3 }} activeDot={{ r: 5 }} />
        ))}
      </LineChart>
    </ResponsiveContainer>
  );
};

const BarChartRenderer = ({ config, data, colors }) => {
  const series = config.seriesFields || [config.yAxis?.field || 'value'];
  const labels = config.seriesLabels || {};
  const height = config.height || 250;
  const useThresholdColors = config.thresholds && series.length === 1;

  return (
    <ResponsiveContainer width="100%" height={height}>
      <BarChart data={data} margin={{ top: 5, right: 20, bottom: 5, left: 0 }}>
        <CartesianGrid strokeDasharray="3 3" stroke={colors.border} />
        <XAxis dataKey={config.xAxis?.field || 'label'} tick={{ fill: colors.textMuted, fontSize: 11 }} stroke={colors.border} />
        <YAxis tick={{ fill: colors.textMuted, fontSize: 11 }} stroke={colors.border} />
        <Tooltip contentStyle={{ background: colors.surface, border: `1px solid ${colors.border}`, borderRadius: 4, color: colors.text }} />
        {series.length > 1 && <Legend wrapperStyle={{ color: colors.textSecondary, fontSize: 11 }} />}
        {config.thresholds?.target && (
          <ReferenceLine y={config.thresholds.target} stroke="#22c55e" strokeDasharray="5 5" label={{ value: 'Target', fill: '#22c55e', fontSize: 10 }} />
        )}
        {series.map((field, i) => (
          <Bar key={field} dataKey={field} name={labels[field] || field}
            fill={CHART_COLORS[i % CHART_COLORS.length]} radius={[4, 4, 0, 0]}>
            {useThresholdColors && data.map((entry, idx) => (
              <Cell key={idx} fill={getThresholdColor(entry[field], config.thresholds)} />
            ))}
          </Bar>
        ))}
      </BarChart>
    </ResponsiveContainer>
  );
};

const AreaChartRenderer = ({ config, data, colors }) => {
  const series = config.seriesFields || [config.yAxis?.field || 'value'];
  const labels = config.seriesLabels || {};
  const height = config.height || 250;

  return (
    <ResponsiveContainer width="100%" height={height}>
      <AreaChart data={data} margin={{ top: 5, right: 20, bottom: 5, left: 0 }}>
        <CartesianGrid strokeDasharray="3 3" stroke={colors.border} />
        <XAxis dataKey={config.xAxis?.field || 'date'} tick={{ fill: colors.textMuted, fontSize: 11 }} stroke={colors.border} />
        <YAxis tick={{ fill: colors.textMuted, fontSize: 11 }} stroke={colors.border} />
        <Tooltip contentStyle={{ background: colors.surface, border: `1px solid ${colors.border}`, borderRadius: 4, color: colors.text }} />
        {series.map((field, i) => (
          <Area key={field} type="monotone" dataKey={field} name={labels[field] || field}
            stroke={CHART_COLORS[i % CHART_COLORS.length]} fill={CHART_COLORS[i % CHART_COLORS.length]}
            fillOpacity={0.15} strokeWidth={2} />
        ))}
      </AreaChart>
    </ResponsiveContainer>
  );
};

const PieChartRenderer = ({ config, data, colors }) => {
  const height = config.height || 250;
  const labelField = config.xAxis?.field || 'label';
  const valueField = config.yAxis?.field || config.valueField || 'value';

  return (
    <ResponsiveContainer width="100%" height={height}>
      <PieChart>
        <Pie data={data} dataKey={valueField} nameKey={labelField}
          cx="50%" cy="50%" outerRadius={height * 0.35} innerRadius={height * 0.18}
          paddingAngle={2} label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
          labelLine={{ stroke: colors.textMuted }}>
          {data.map((_, i) => (
            <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
          ))}
        </Pie>
        <Tooltip contentStyle={{ background: colors.surface, border: `1px solid ${colors.border}`, borderRadius: 4, color: colors.text }} />
      </PieChart>
    </ResponsiveContainer>
  );
};

const GaugeRenderer = ({ config, data, colors }) => {
  const value = data?.[config.valueField || 'value'] ?? data?.value ?? 0;
  const max = config.max || 1.0;
  const pct = Math.min(value / max, 1.0);
  const color = getThresholdColor(value, config.thresholds);
  const height = config.height || 120;

  // Simple arc gauge using SVG
  const radius = 50;
  const circumference = Math.PI * radius; // half circle
  const offset = circumference * (1 - pct);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height }}>
      <svg width={130} height={75} viewBox="0 0 130 75">
        {/* Background arc */}
        <path d="M 15 70 A 50 50 0 0 1 115 70" fill="none" stroke={colors.border} strokeWidth="10" strokeLinecap="round" />
        {/* Value arc */}
        <path d="M 15 70 A 50 50 0 0 1 115 70" fill="none" stroke={color} strokeWidth="10" strokeLinecap="round"
          strokeDasharray={`${circumference}`} strokeDashoffset={offset} />
        {/* Value text */}
        <text x="65" y="60" textAnchor="middle" fill={color} fontSize="20" fontWeight="bold">
          {typeof value === 'number' ? (value < 10 ? value.toFixed(2) : Math.round(value)) : value}
        </text>
      </svg>
    </Box>
  );
};

const NumberCardRenderer = ({ config, data, colors }) => {
  const value = data?.[config.valueField || 'value'] ?? data?.value ?? 0;
  const trendPct = data?.[config.trendField || 'trendPct'] ?? data?.trendPct ?? null;
  const color = getThresholdColor(value, config.thresholds);

  const TrendIcon = trendPct > 1 ? TrendingUpIcon : trendPct < -1 ? TrendingDownIcon : TrendingFlatIcon;
  const trendColor = trendPct > 0 ? '#22c55e' : trendPct < 0 ? '#ef4444' : colors.textMuted;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 1 }}>
      <Typography variant="h3" sx={{ color, fontWeight: 700, lineHeight: 1 }}>
        {typeof value === 'number' ? (Number.isInteger(value) ? value : value.toFixed(1)) : value}
      </Typography>
      {config.unit && (
        <Typography variant="caption" sx={{ color: colors.textMuted, mt: 0.25 }}>{config.unit}</Typography>
      )}
      {trendPct !== null && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, mt: 0.5 }}>
          <TrendIcon sx={{ fontSize: 16, color: trendColor }} />
          <Typography variant="caption" sx={{ color: trendColor, fontWeight: 600 }}>
            {trendPct > 0 ? '+' : ''}{typeof trendPct === 'number' ? trendPct.toFixed(1) : trendPct}%
          </Typography>
        </Box>
      )}
    </Box>
  );
};

// ========== Main Component ==========

const WorkspaceChart = ({ config, colors, context, onNavigateToView }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const contextRef = React.useRef(context);
  contextRef.current = context;

  const loadData = useCallback(async () => {
    if (!config.dataScript) {
      setData(config.staticData || []);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const result = await FlexService.executeScript({
        scriptName: config.dataScript,
        context: contextRef.current || {},
      });
      const chartData = result?.context?.result || result?.result || [];
      setData(chartData);
      setError(null);
    } catch (err) {
      console.error(`Chart data script failed (${config.dataScript}):`, err);
      setError(err.message);
      // Use demo data on error so UI isn't blank
      setData(config.demoData || []);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config.dataScript, config.staticData, config.demoData]);

  useEffect(() => {
    loadData();

    // Auto-refresh
    if (config.refreshInterval) {
      const interval = setInterval(loadData, config.refreshInterval * 1000);
      return () => clearInterval(interval);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config.dataScript]);

  const renderChart = () => {
    if (!data) return null;

    switch (config.chartType) {
      case 'LINE':        return <LineChartRenderer config={config} data={data} colors={colors} />;
      case 'BAR':         return <BarChartRenderer config={config} data={data} colors={colors} />;
      case 'STACKED_BAR': return <BarChartRenderer config={{ ...config, stacked: true }} data={data} colors={colors} />;
      case 'AREA':        return <AreaChartRenderer config={config} data={data} colors={colors} />;
      case 'PIE':         return <PieChartRenderer config={config} data={data} colors={colors} />;
      case 'GAUGE':       return <GaugeRenderer config={config} data={data} colors={colors} />;
      case 'NUMBER':      return <NumberCardRenderer config={config} data={data} colors={colors} />;
      default:            return <Typography variant="caption" sx={{ color: colors.textMuted }}>Unknown chart type: {config.chartType}</Typography>;
    }
  };

  const isClickable = config.linkToView && onNavigateToView;

  const handleClick = () => {
    if (isClickable) {
      onNavigateToView(config.linkToView);
    }
  };

  return (
    <Box
      onClick={handleClick}
      sx={{
        border: `1px solid ${colors.border}`,
        borderRadius: 1,
        bgcolor: colors.surface || colors.background,
        overflow: 'hidden',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        ...(isClickable && {
          cursor: 'pointer',
          '&:hover': {
            borderColor: '#22c55e',
            bgcolor: `${colors.surface || colors.background}ee`,
          },
        }),
      }}
    >
      {/* Title bar */}
      <Box sx={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        px: 1.5, py: 0.75, borderBottom: `1px solid ${colors.border}`,
      }}>
        <Typography variant="caption" sx={{ color: colors.textSecondary, fontWeight: 600, fontSize: '0.75rem' }}>
          {config.title || 'Chart'}
          {isClickable && <span style={{ marginLeft: 4, fontSize: '0.65rem', color: colors.textMuted }}>&#8599;</span>}
        </Typography>
        {loading && <CircularProgress size={12} sx={{ color: colors.textMuted }} />}
      </Box>

      {/* Chart content */}
      <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', p: 1, minHeight: config.chartType === 'NUMBER' ? 80 : config.chartType === 'GAUGE' ? 100 : 200 }}>
        {error && !data ? (
          <Typography variant="caption" sx={{ color: colors.error || '#ef4444' }}>Failed to load: {error}</Typography>
        ) : loading && !data ? (
          <CircularProgress size={24} sx={{ color: colors.textMuted }} />
        ) : (
          renderChart()
        )}
      </Box>
    </Box>
  );
};

export default WorkspaceChart;
